import { Comment } from "@/components/comments/Comment.tsx";
import { FormResultForEmployeeProps } from "@/components/form-result/result-modal/components/types.ts";
import { CommentElement } from "@/components/questionnaire/components/CommentElement.tsx";
import { ScaleOptionButtons } from "@/components/questionnaire/components/ScaleOptionButtons.tsx";
import { useScaleDirection } from "@/components/questionnaire/components/use-scale-direction.hook.ts";
import { getFormKey } from "@/components/questionnaire/questionnaire.types.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { Stack, Title, HtmlViewer } from "@skailer-com/uikit";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ScaleElementProps {
  element: ApiFormViewElement;
}

export const ScaleElement = ({ element }: ScaleElementProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const firstDirection = useScaleDirection(element.scale_settings?.form_scale?.scale);
  const secondDirection = useScaleDirection(element.scale_settings?.form_scale.scale2);

  const scaleSettings = element.scale_settings;
  if (!scaleSettings) return null;

  const scaleFirst = scaleSettings.form_scale.scale.sort((a, b) => a.sort - b.sort);
  const scaleSecond = scaleSettings.form_scale.scale2?.sort((a, b) => a.sort - b.sort);

  const formElementName = getFormKey(element);

  const hasError = !!errors[formElementName];

  return (
    <Stack gap={"2xl"} data-error={hasError || undefined}>
      {(scaleSettings.title || scaleSettings.description) && (
        <Stack gap="md">
          <Title
            header={scaleSettings.title || undefined}
            size={"md"}
            required={scaleSettings.is_required}
          />
          {scaleSettings.description && <HtmlViewer content={scaleSettings.description} />}
        </Stack>
      )}

      <Stack gap={"md"}>
        <Controller
          name={formElementName}
          control={control}
          render={({ field }) => {
            const values = Array.isArray(field.value) ? (field.value as number[]) : [];
            const secondIds = scaleSecond?.map((s) => s.id) || [];
            const handleFirst = (id: number) =>
              field.onChange([...values.filter((v) => secondIds.includes(v)), id]);
            return (
              <ScaleOptionButtons
                scale={scaleFirst}
                selectedIds={values}
                direction={firstDirection}
                onSelect={handleFirst}
                error={String(errors[formElementName]?.message || "")}
              />
            );
          }}
        />
        {scaleSecond && (
          <Controller
            name={formElementName}
            control={control}
            render={({ field }) => {
              const values = Array.isArray(field.value) ? (field.value as number[]) : [];
              const firstIds = scaleFirst.map((s) => s.id);
              const handleSecond = (id: number) =>
                field.onChange([...values.filter((v) => firstIds.includes(v)), id]);

              return (
                <ScaleOptionButtons
                  scale={scaleSecond}
                  selectedIds={values}
                  direction={secondDirection}
                  onSelect={handleSecond}
                  error={String(errors[formElementName]?.message || "")}
                />
              );
            }}
          />
        )}
      </Stack>
      {scaleSettings.is_comment_enabled && <CommentElement element={element} />}
    </Stack>
  );
};

export const ScaleElementResult = ({ element, answer }: FormResultForEmployeeProps) => {
  const { t } = useTranslation();

  const firstDirection = useScaleDirection(element.scale_settings?.form_scale?.scale);
  const secondDirection = useScaleDirection(element.scale_settings?.form_scale.scale2);

  const scaleSettings = element.scale_settings;
  if (!scaleSettings) return null;

  const selectedIds = answer?.answer_scale_option_ids || [];
  const scaleFirst = scaleSettings.form_scale.scale.sort((a, b) => a.sort - b.sort);
  const scaleSecond = scaleSettings.form_scale.scale2?.sort((a, b) => a.sort - b.sort);
  return (
    <>
      <Title
        header={scaleSettings.title || t("surveys:question_type_scale")}
        size={"sm"}
        paddingTop={true}
        className={!scaleSettings.title ? "text-secondary" : ""}
      />
      <Stack gap={"md"} className={"mt-3"}>
        <ScaleOptionButtons
          scale={scaleFirst}
          selectedIds={selectedIds}
          direction={firstDirection}
        />

        {scaleSecond && (
          <ScaleOptionButtons
            scale={scaleSecond}
            selectedIds={selectedIds}
            direction={secondDirection}
          />
        )}
        {answer && answer.comment && (
          <Comment text={answer.comment} employee={null} isShort={false} />
        )}
      </Stack>
    </>
  );
};
